<template>
  <div class="pt-2">
    <v-row dense>
      <v-col cols="12" md="12" class="px-3">
        <v-card flat outlined class="mb-3">
          <v-card-title>Network Metrics</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="networkMetricsHeaders"
              :items="networkMetricsItems"
              hide-default-footer
              class="elevation-1"
            ></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" md="12" class="px-3">
        <v-card flat>
          <v-card height="240">
            <ProjectDiagram
              :network.sync="diagramNetwork"
              @update-breakers="updateBreakers"
              @click-switch="updateSwitch"
            />
          </v-card>
          <v-card-actions>
            <v-spacer />
            <v-btn
              x-small
              rounded
              outlined
              color="primary"
              :to="{
                name: 'CaseVis',
                hash: '#NetworkDiagram',
                params: { id: currentCase.id, folderId: computedFolderId },
              }"
              >Open diagram</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="12">
        <Aggregate
          title="Nodes"
          v-model="computedInput.nodes"
          :ItemConstructor="nodeFactory"
          :item-defaults="nodeDefault"
          :headers="nodeHeaders"
          v-bind="$attrs"
          copy-btn
          @change="$emit('change')"
        >
          <template v-slot:[`item.rated_voltage`]="{ item }">
            <v-text-field
              v-model.number="item.rated_voltage"
              :hint="schemaNode.rated_voltage.title"
              :suffix="schemaNode.rated_voltage.unit"
              type="number"
              single-line
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.max_voltage`]="{ item }">
            <v-text-field
              v-model.number="item.max_voltage"
              :hint="schemaNode.max_voltage.title"
              :suffix="schemaNode.max_voltage.unit"
              type="number"
              single-line
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.min_voltage`]="{ item }">
            <v-text-field
              v-model.number="item.min_voltage"
              :hint="schemaNode.min_voltage.title"
              :suffix="schemaNode.min_voltage.unit"
              type="number"
              single-line
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.active`]="{ item }">
            <v-switch
              v-model="item.active"
              :label="schemaNode.active.title"
              dense
              @change="$emit('change')"
            ></v-switch>
          </template>
          <template #default="{ item }">
            <v-card class="mb-3">
              <v-card-text>
                <InputGeo v-model="item.coordinates" :icon="item.icon">
                  <template #append-row>
                    <InputGeneric label="Icon" :label-size="1">
                      <InputIcon v-model="item.icon" @change="$emit('change')"></InputIcon>
                    </InputGeneric>
                  </template>
                </InputGeo>
              </v-card-text> </v-card></template
        ></Aggregate> </v-col
    ></v-row>
    <v-row dense>
      <v-col cols="12" lg="12">
        <Aggregate
          title="Branches"
          v-model="computedInput.branches"
          :ItemConstructor="branchFactory"
          :headers="branchHeaders"
          v-bind="$attrs"
          copy-btn
          @change="$emit('change')"
        >
          <template v-slot:[`item.node_from`]="{ item }">
            <v-select
              v-model="item.node_from"
              :items="nodeIndex"
              :hint="schemaBranch.node_from.tooltip"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.node_to`]="{ item }">
            <v-select
              v-model="item.node_to"
              :items="nodeIndex"
              :hint="schemaBranch.node_to.tooltip"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.length`]="{ item }">
            <v-text-field
              v-model.number="item.length"
              :suffix="schemaBranch.length.unit"
              type="number"
              single-line
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.standard_type`]="{ item }">
            <v-select
              v-model="item.standard_type"
              :items="lineStandardTypesNames"
              :label="schemaBranch.standard_type.title"
              @change="standardType(item.id, 'branches')"
              clearable
              single-line
            />
          </template>
          <template v-slot:[`item.active`]="{ item }">
            <v-switch
              v-model="item.active"
              :label="schemaBranch.active.title"
              dense
              @change="$emit('change')"
            ></v-switch>
          </template>
          <template #default="{ item }">
            <v-card class="mb-3">
              <v-card-title>
                <h6 class="text-h6">Parameters</h6>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.reactance"
                          :label="schemaBranch.reactance.title"
                          :suffix="schemaBranch.reactance.unit"
                          type="number"
                          :disabled="disable"
                          @change="$emit('change')"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.capacitance"
                          :label="schemaBranch.capacitance.title"
                          :suffix="schemaBranch.capacitance.unit"
                          type="number"
                          :disabled="disable"
                          @change="$emit('change')"
                        />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.max_thermal_current"
                          :label="schemaBranch.max_thermal_current.title"
                          :suffix="schemaBranch.max_thermal_current.unit"
                          type="number"
                          :disabled="disable"
                          @change="$emit('change')"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.max_loading_percent"
                          :label="schemaBranch.max_loading_percent.title"
                          :suffix="schemaBranch.max_loading_percent.unit"
                          type="number"
                          @change="$emit('change')"
                        />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.resistance"
                          :label="schemaBranch.resistance.title"
                          :suffix="schemaBranch.resistance.unit"
                          type="number"
                          :disabled="disable"
                          @change="$emit('change')"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="item.voltage"
                          :label="schemaBranch.voltage.title"
                          :suffix="schemaBranch.voltage.unit"
                          type="number"
                          @change="$emit('change')"
                        />
                      </v-col>
                    </v-row>

                    <InputYearly
                      v-model="item.capacity"
                      :label="schemaBranch.capacity.title"
                      :num_years="num_years"
                      :suffix="schemaBranch.capacity.unit"
                      spread-values
                      fill
                      :fill-value="schemaBranch.capacity.items.default"
                      :start-year="currentCase.settings.year_start"
                      type="number"
                      @change="$emit('change')"
                    >
                    </InputYearly>
                    <v-switch
                      v-model="item.run_contingency"
                      label="Run contingency"
                      hint="Consider this branch in an n-1 contingency analysis"
                      persistent-hint
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h6 class="text-h6">
                      Transmission Line
                      <Dialog
                        icon="edit"
                        tooltip="Edit transmission line"
                        :width="500"
                        title="Transmission Line"
                        :body="{
                          component: inputCoordinates,
                          props: {
                            value: item.coordinates,
                            headers: [
                              { name: 'Lat', editable: true },
                              { name: 'Lon', editable: true },
                            ],
                          },
                        }"
                        v-on="$listeners"
                      ></Dialog>
                    </h6>
                    <LMap
                      :options="{ attributionControl: false }"
                      style="z-index: 0"
                      @ready="centerMap(`branch-${item.id}`, item)"
                      :ref="`branch-${item.id}`"
                    >
                      <LTileLayer :url="mapUrl" />
                      <LMarker
                        v-if="getNode(item.node_from)"
                        :lat-lng="getNode(item.node_from).coordinates"
                        :icon="circleIcon(getNode(item.node_from).icon, 'black')"
                        ><LTooltip
                          :options="{
                            permanent: true,
                            interactive: true,
                            direction: 'top',
                            offset: point(0, -20),
                          }"
                          ><div>
                            {{ getNode(item.node_from).name }}
                          </div>
                        </LTooltip>
                      </LMarker>
                      <LMarker
                        v-if="getNode(item.node_to)"
                        :lat-lng="getNode(item.node_to).coordinates"
                        :icon="circleIcon(getNode(item.node_to).icon, 'black')"
                        ><LTooltip
                          :options="{
                            permanent: true,
                            interactive: true,
                            direction: 'top',
                            offset: point(0, -20),
                          }"
                          ><div>
                            {{ getNode(item.node_to).name }}
                          </div>
                        </LTooltip>
                      </LMarker>
                      <LPolyline :lat-lngs="getBranchCoordinates(item)" color="#424242" />
                      <LControl position="bottomright" class="logo-control-2">
                        <img src="@/assets/rex-logo.png" alt="Acelerex" class="logo-2" />
                      </LControl>
                    </LMap>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-title>
                <h6 class="text-h6">Short Circuit Parameters</h6>
              </v-card-title>
              <v-card-text>
                <v-text-field
                  v-model.number="item.end_temperature"
                  :label="schemaBranch.end_temperature.title"
                  :hint="schemaBranch.end_temperature.tooltip"
                  :suffix="schemaBranch.end_temperature.unit"
                  type="number"
                  @change="$emit('change')"
                />
              </v-card-text>
            </v-card>
          </template>
        </Aggregate>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="12">
        <Aggregate
          title="Transformers"
          v-model="computedInput.transformers"
          :ItemConstructor="transformerFactory"
          :headers="transformerHeaders"
          v-bind="$attrs"
          copy-btn
          @change="$emit('change')"
        >
          <template v-slot:[`item.winding`]="{ item }">
            <v-select
              v-model="item.winding"
              :items="windingDropDown"
              item-text="text"
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.high_voltage_node`]="{ item }">
            <v-select
              v-model="item.high_voltage_node"
              :items="nodeIndex"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.low_voltage_node`]="{ item }">
            <v-select
              v-model="item.low_voltage_node"
              :items="nodeIndex"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.medium_voltage_node`]="{ item }">
            <v-select
              v-model="item.medium_voltage_node"
              :items="nodeIndex"
              :disabled="!(item.winding === 'three')"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.standard_type`]="{ item }">
            <v-select
              v-model="item.standard_type"
              :items="
                item.winding === 'two'
                  ? transformerStandardTypesNames
                  : transformer3wStandardTypesNames
              "
              :label="schemaTransformer.standard_type.title"
              @change="standardType(item.id, 'transformers')"
              single-line
              clearable
            />
          </template>
          <template #default="{ item }">
            <v-card class="mb-3">
              <v-card-title>
                <h6 class="text-h6">Parameters</h6>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-switch
                    v-model="item.active"
                    :label="schemaTransformer.active.title"
                    dense
                    class="mr-8 mb-4"
                    @change="$emit('change')"
                  ></v-switch>
                  <v-switch
                    v-model="item.run_contingency"
                    label="Run contingency"
                    hint="Consider this transformer in an n-1 contingency analysis"
                    persistent-hint
                  ></v-switch>
                </v-row>
                <v-text-field
                  v-model.number="item.hv_node_rated_voltage"
                  :label="schemaTransformer.hv_node_rated_voltage.title"
                  :suffix="schemaTransformer.hv_node_rated_voltage.unit"
                  type="number"
                  :disabled="disable"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-if="item.winding === 'three'"
                  v-model.number="item.mv_node_rated_voltage"
                  :label="schemaTransformer3w.mv_node_rated_voltage.title"
                  :suffix="schemaTransformer3w.mv_node_rated_voltage.unit"
                  type="number"
                  :disabled="disable"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-model.number="item.lv_node_rated_voltage"
                  :label="schemaTransformer.lv_node_rated_voltage.title"
                  :suffix="schemaTransformer.lv_node_rated_voltage.unit"
                  type="number"
                  :disabled="disable"
                  @change="$emit('change')"
                />
                <template v-if="item.winding === 'two'">
                  <v-text-field
                    v-model.number="item.rated_apparant_power"
                    :label="schemaTransformer.rated_apparant_power.title"
                    :suffix="schemaTransformer.rated_apparant_power.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.short_circuit_voltage"
                    :label="schemaTransformer.short_circuit_voltage.title"
                    :suffix="schemaTransformer.short_circuit_voltage.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.short_circuit_real_voltage"
                    :label="schemaTransformer.short_circuit_real_voltage.title"
                    :suffix="schemaTransformer.short_circuit_real_voltage.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.max_loading_percent"
                    :label="schemaTransformer.max_loading_percent.title"
                    :suffix="schemaTransformer.max_loading_percent.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.phase_shift_angle"
                    :label="schemaTransformer.phase_shift_angle.title"
                    :suffix="schemaTransformer.phase_shift_angle.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <template v-if="item.winding === 'three'">
                  <v-text-field
                    v-model.number="item.hv_node_rated_apparent_power"
                    :label="schemaTransformer3w.hv_node_rated_apparent_power.title"
                    :suffix="schemaTransformer3w.hv_node_rated_apparent_power.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.mv_node_rated_apparent_power"
                    :label="schemaTransformer3w.mv_node_rated_apparent_power.title"
                    :suffix="schemaTransformer3w.mv_node_rated_apparent_power.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.lv_node_rated_apparent_power"
                    :label="schemaTransformer3w.lv_node_rated_apparent_power.title"
                    :suffix="schemaTransformer3w.lv_node_rated_apparent_power.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_voltage_hv_to_mv"
                    :label="schemaTransformer3w.SC_voltage_hv_to_mv.title"
                    :suffix="schemaTransformer3w.SC_voltage_hv_to_mv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_voltage_mv_to_lv"
                    :label="schemaTransformer3w.SC_voltage_mv_to_lv.title"
                    :suffix="schemaTransformer3w.SC_voltage_mv_to_lv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_voltage_hv_to_lv"
                    :label="schemaTransformer3w.SC_voltage_hv_to_lv.title"
                    :suffix="schemaTransformer3w.SC_voltage_hv_to_lv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_real_voltage_hv_to_mv"
                    :label="schemaTransformer3w.SC_real_voltage_hv_to_mv.title"
                    :suffix="schemaTransformer3w.SC_real_voltage_hv_to_mv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_real_voltage_mv_to_lv"
                    :label="schemaTransformer3w.SC_real_voltage_mv_to_lv.title"
                    :suffix="schemaTransformer3w.SC_real_voltage_mv_to_lv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.SC_real_voltage_hv_to_lv"
                    :label="schemaTransformer3w.SC_real_voltage_hv_to_lv.title"
                    :suffix="schemaTransformer3w.SC_real_voltage_hv_to_lv.unit"
                    type="number"
                    :disabled="disable"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.mv_phase_shift_angle"
                    :label="schemaTransformer3w.mv_phase_shift_angle.title"
                    :suffix="schemaTransformer3w.mv_phase_shift_angle.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.lv_phase_shift_angle"
                    :label="schemaTransformer3w.lv_phase_shift_angle.title"
                    :suffix="schemaTransformer3w.lv_phase_shift_angle.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </template>
                <v-text-field
                  v-model.number="item.iron_losses"
                  :label="schemaTransformer.iron_losses.title"
                  :suffix="schemaTransformer.iron_losses.unit"
                  type="number"
                  :disabled="disable"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-model.number="item.open_loop_losses"
                  :label="schemaTransformer.open_loop_losses.title"
                  :suffix="schemaTransformer.open_loop_losses.unit"
                  type="number"
                  @change="$emit('change')"
                />
              </v-card-text>
              <v-card-title>
                <h6 class="text-h6">Tap Changer Parameters</h6>
              </v-card-title>
              <v-card-text>
                <v-switch
                  v-model="item.ideal_phase_shifter"
                  :label="schemaTransformer.ideal_phase_shifter.title"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-select
                  v-model="item.tap_changer_side"
                  clearable
                  :items="transformerTapChangerSideItems(item)"
                  :label="schemaTransformer.tap_changer_side.title"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-model.number="item.rated_tap_position"
                  :label="schemaTransformer.rated_tap_position.title"
                  :suffix="schemaTransformer.rated_tap_position.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-model.number="item.min_tap_position"
                  :label="schemaTransformer.min_tap_position.title"
                  :suffix="schemaTransformer.min_tap_position.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-text-field
                  v-model.number="item.max_tap_position"
                  :label="schemaTransformer.max_tap_position.title"
                  :suffix="schemaTransformer.max_tap_position.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-row>
                  <v-col v-if="item.ideal_phase_shifter">
                    <v-radio-group v-model="tapStep" column>
                      <v-radio label="Tap Step Size for Voltage Angle" value="angle"></v-radio>
                      <v-radio
                        label="Tap Step Size for Voltage Magnitude"
                        value="magnitude"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-if="!item.ideal_phase_shifter || tapStep === 'angle'"
                      v-model.number="item.tap_step_size_degree"
                      :label="schemaTransformer.tap_step_size_degree.title"
                      :suffix="schemaTransformer.tap_step_size_degree.unit"
                      type="number"
                      @change="$emit('change')"
                    />
                    <v-text-field
                      v-if="!item.ideal_phase_shifter || tapStep === 'magnitude'"
                      v-model.number="item.tap_step_size_percentage"
                      :label="schemaTransformer.tap_step_size_percentage.title"
                      :suffix="schemaTransformer.tap_step_size_percentage.unit"
                      type="number"
                      @change="$emit('change')"
                    />
                  </v-col>
                </v-row>

                <v-text-field
                  v-model.number="item.tap_changer_position"
                  :label="schemaTransformer.tap_changer_position.title"
                  :suffix="schemaTransformer.tap_changer_position.unit"
                  type="number"
                  @change="$emit('change')"
                />
                <v-select
                  v-model="item.vector_group"
                  :items="schemaTransformer.vector_group.enum.filter((el) => el !== null)"
                  :hint="schemaTransformer.vector_group.tooltip"
                  :label="schemaTransformer.vector_group.title"
                  :disabled="disable"
                  @change="$emit('change')"
                />
              </v-card-text>
              <template v-if="item.winding === 'two'">
                <v-card-title>
                  <h6 class="text-h6">Short Circuit Parameters</h6>
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model.number="item.zs_voltage"
                    :label="schemaTransformer.zs_voltage.title"
                    :suffix="schemaTransformer.zs_voltage.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.zs_real_voltage"
                    :label="schemaTransformer.zs_real_voltage.title"
                    :suffix="schemaTransformer.zs_real_voltage.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.zs_magnetizing_impedance"
                    :label="schemaTransformer.zs_magnetizing_impedance.title"
                    :suffix="schemaTransformer.zs_magnetizing_impedance.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.zs_magnetizing_rx_ratio"
                    :label="schemaTransformer.zs_magnetizing_rx_ratio.title"
                    :suffix="schemaTransformer.zs_magnetizing_rx_ratio.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                  <v-text-field
                    v-model.number="item.hv_node_zs_impedance_distribution"
                    :label="schemaTransformer.hv_node_zs_impedance_distribution.title"
                    :suffix="schemaTransformer.hv_node_zs_impedance_distribution.unit"
                    type="number"
                    @change="$emit('change')"
                  />
                </v-card-text>
              </template>
            </v-card>
          </template>
        </Aggregate>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="12">
        <Aggregate
          title="Switches"
          v-model="computedInput.switches"
          :ItemConstructor="switchFactory"
          :headers="switchHeaders"
          v-bind="$attrs"
          copy-btn
          noExpand
          @change="$emit('change')"
        >
          <template v-slot:[`item.bus_id`]="{ item }">
            <v-select
              v-model="item.bus_id"
              :items="nodeIndex"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.element_type`]="{ item }">
            <v-select
              v-model="item.element_type"
              :items="switchElementTypeItems"
              item-text="text"
              single-line
              @change="$emit('change')"
            />
          </template>
          <template v-slot:[`item.element`]="{ item }">
            <v-select
              v-model="item.element"
              :items="switchElementSelectionMethod(item.element_type)"
              item-text="text"
              single-line
              @change="$emit('change')"
            >
              <template #selection="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
              <template #item="data">
                {{ data.item.name }}
                <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.type`]="{ item }">
            <v-select
              v-model="item.type"
              :items="switchTypeItems"
              item-text="text"
              clearable
              single-line
              @change="$emit('change')"
            />
          </template>
          <template #[`item.closed`]="{ item }">
            <v-switch v-model="item.closed" dense @change="$emit('change')"></v-switch>
          </template>
        </Aggregate>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import lineStandardTypes from "@/assets/lineTypes.json";
import transformer3wStandardTypes from "@/assets/trafo3wTypes.json";
import transformerStandardTypes from "@/assets/trafoTypes.json";
import InputCoordinates from "@/components/InputCoordinates.vue";
import { circleIcon } from "@/plugins/map-utils";
import { CaseModel, schemas } from "data-dictionary";
import { latLngBounds, Point } from "leaflet";
import { LControl, LMap, LMarker, LPolyline, LTileLayer, LTooltip } from "vue2-leaflet";

export default {
  name: "SystemNetwork",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputGeneric: () => import("@/components/inputs/InputGeneric.vue"),
    InputIcon: () => import("@/components/inputs/InputIcon.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    ProjectDiagram: () => import("@/components/ProjectDiagram.vue"),
    Dialog: () => import("@/components/Dialog.vue"),
    LMap,
    LMarker,
    LTooltip,
    LTileLayer,
    LControl,
    LPolyline,
  },
  mixins: [],
  props: {
    currentCase: {
      type: Object,
      required: true,
    },
    input: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
    num_years: { type: Number, required: true },
  },
  data() {
    return {
      tapStep: "angle",
      networkMetricsHeaders: [
        {
          text: "Node Count",
          align: "start",
          value: "node",
        },
        { text: "Branch Count", value: "branch" },
        { text: "Transformer Count", value: "transformer" },
        { text: "Generator Count", value: "generator" },
        { text: "Demand Count", value: "demand" },
        { text: "Switch Count", value: "switch" },
        { text: "Storage Count", value: "storage" },
      ],
      switchElementTypeItems: [
        { text: "Node-Node", value: "b" },
        { text: "Node-Branch", value: "l" },
        { text: "Node-Transformer", value: "t" },
      ],
      windingDropDown: [
        {
          text: "Three",
          value: "three",
        },
        {
          text: "Two",
          value: "two",
        },
      ],
      switchHeaders: CaseModel.Switch.HEADERS,
      transformerHeaders: CaseModel.Transformer.HEADERS,
      nodeHeaders: CaseModel.Node.HEADERS,
      branchHeaders: CaseModel.Branch.HEADERS,
      disable: false,
      schemaNode: {
        ...schemas.find((v) => v.$id === "node.schema.json").properties,
      },
      schemaBranch: {
        ...schemas.find((v) => v.$id === "branch.schema.json").properties,
      },
      schemaTransformer: {
        ...schemas.find((v) => v.$id === "transformer.schema.json").properties,
      },
      schemaTransformer3w: {
        ...schemas.find((v) => v.$id === "transformer3w.schema.json").properties,
      },
      schemaSwitch: {
        ...schemas.find((v) => v.$id === "switches.schema.json").properties,
      },
      lineStandardTypesNames: lineStandardTypes.map((el) => el.name),
      transformerStandardTypesNames: transformerStandardTypes.map((el) => el.name),
      transformer3wStandardTypesNames: transformer3wStandardTypes.map((el) => el.name),
      mapUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      inputCoordinates: InputCoordinates,
    };
  },
  computed: {
    networkMetricsItems() {
      return [
        {
          node: this.computedInput.nodes.length,
          transformer: this.computedInput.transformers.length,
          branch: this.computedInput.branches.length,
          switch: this.computedInput.switches.length,
          generator: this.computedInput.generators.filter((el) => el.bus_id).length,
          demand: this.computedInput.demands.length,
          storage: this.computedInput.storage.filter((el) => el.bus_id).length,
        },
      ];
    },
    computedFolderId() {
      return this.currentCase.directory_id;
    },
    diagramNetwork: {
      get() {
        if (!this.input) return {};

        const branches = [];
        const transformers = [];
        const switches = [];

        this.input.branches.forEach((branch) => {
          if (branch.node_from && branch.node_to) {
            branches.push({
              id: branch.id,
              name: branch.name,
              fromBus: branch.node_from,
              toBus: branch.node_to,
            });
          }
        });
        this.input.transformers.forEach((transformer) => {
          if (transformer.high_voltage_node && transformer.low_voltage_node) {
            const branchId = branches.length + 1;
            branches.push({
              id: branchId,
              name: transformer.name,
              fromBus: transformer.high_voltage_node,
              toBus: transformer.low_voltage_node,
            });
            transformers.push({
              id: transformer.id,
              branchId,
            });
          }
        });
        this.input.switches.forEach((switchObj) => {
          if (switchObj.bus_id && switchObj.element) {
            const branchId = branches.length + 1;
            branches.push({
              id: branchId,
              name: switchObj.name,
              fromBus: switchObj.bus_id,
              toBus: switchObj.element,
            });
            switches.push({
              id: switchObj.id,
              branchId,
              state: switchObj.closed ? "close" : "open",
            });
          }
        });

        return {
          bus: this.input.nodes.map((v) => ({ id: v.id, name: v.name })),
          busLocation: this.input.nodes
            .filter((v) => v.diagramPosition && v.diagramPosition.x && v.diagramPosition.y)
            .map((v) => ({ busId: v.id, x: v.diagramPosition.x, y: v.diagramPosition.y })),
          branch: branches,
          storages: this.input.storage
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
            })),
          loads: this.input.demands
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
            })),
          markets: this.input.markets
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
            })),
          generators: this.input.generators
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
              type: v.type === "THERMAL" ? "THERMAL" : v.source,
            })),
          transformers,
          switches,
        };
      },
      set(v) {
        const inputCopy = this.input;
        const nodes = [];
        if (!inputCopy.nodes) return;
        inputCopy.nodes.forEach((node) => {
          const diagramBus = v.bus.find((b) => b.id === node.id);
          nodes.push({
            ...node,
            ...(diagramBus ? { diagramPosition: { x: diagramBus.x, y: diagramBus.y } } : {}),
          });
        });
        inputCopy.nodes = nodes;
        this.input = inputCopy;
      },
    },
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    branchIndex() {
      return this.computedInput.branches
        ? this.computedInput.branches.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    transformerIndex() {
      const twoWindingTrafo = this.computedInput.transformers.filter(
        (trafo) => trafo.winding === "two"
      );
      return twoWindingTrafo
        ? twoWindingTrafo.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    nodeDefault() {
      return this.computedInput.nodes.length
        ? this.computedInput.nodes[this.computedInput.nodes.length - 1]
        : {};
    },
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    transformerTapChangerSideItems(item) {
      return [
        { text: "High Voltage Side", value: "hv" },
        {
          text: "Medium Voltage Side",
          value: "mv",
          disabled: item.winding === "two",
        },
        { text: "Low Voltage Side", value: "lv" },
      ];
    },
    standardType(itemId, elementType) {
      let stdType = null;
      const inputCopy = this.input;
      const item = inputCopy[elementType].find((el) => el.id === itemId);
      if (item.standard_type) {
        if (elementType === "transformers") {
          stdType =
            item.winding === "two"
              ? transformerStandardTypes.filter((element) => element.name === item.standard_type)
              : transformer3wStandardTypes.filter((element) => element.name === item.standard_type);
        } else {
          stdType = lineStandardTypes.filter((element) => element.name === item.standard_type);
        }
        Object.keys(stdType[0]).forEach((properties) => {
          if (properties !== "name") {
            if (Object.prototype.hasOwnProperty.call(item, properties)) {
              if (Number.isNaN(parseFloat(stdType[0][properties]))) {
                if (stdType[0][properties] === "True") {
                  item[properties] = true;
                } else if (stdType[0][properties] === "False") {
                  item[properties] = false;
                } else {
                  item[properties] = stdType[0][properties];
                }
              } else {
                item[properties] = parseFloat(stdType[0][properties]);
              }
            }
          }
        });
      }
      this.$emit("change");
      this.$emit("input", inputCopy);
    },
    switchElementSelectionMethod(sel) {
      switch (sel) {
        case "l":
          return this.branchIndex;
        case "b":
          return this.nodeIndex;
        case "t":
          return this.transformerIndex;
        default:
          return [];
      }
    },
    switchFactory: CaseModel.Switch,
    transformerFactory: CaseModel.Transformer,
    nodeFactory: CaseModel.Node,
    branchFactory: CaseModel.Branch,
    updateBreakers($e) {
      const { resourceType, resource, state } = $e;
      const networkKey = {
        market: "markets",
        load: "demands",
        generatorThermal: "generators",
        generatorSolar: "generators",
        generatorHydro: "generators",
        generatorWind: "generators",
        storage: "storages",
      };
      const inputCopy = this.input;
      const resourceCopy = inputCopy[networkKey[resourceType]].find((v) => v.id === resource.id);
      const resourceIndex = inputCopy[networkKey[resourceType]].findIndex(
        (v) => v.id === resource.id
      );
      if (resourceCopy) {
        resourceCopy.active = state !== "open";
        inputCopy[networkKey[resourceType]].splice(resourceIndex, 1, resourceCopy);
      }
      this.computedInput = inputCopy;
    },
    updateSwitch($e) {
      const { id, state } = $e.detail;
      const inputCopy = this.input;
      const switchCopy = inputCopy.switches.find((v) => v.id === id);
      const switchIndex = inputCopy.switches.findIndex((v) => v.id === id);
      if (switchCopy) {
        switchCopy.closed = state === "close";
        inputCopy.switches.splice(switchIndex, 1, switchCopy);
      }
      this.computedInput = inputCopy;
    },
    getNode(nodeID) {
      const node = this.computedInput.nodes.find((n) => n.id === nodeID);
      return node || null;
    },
    getNodeCoordinates(nodeID) {
      const node = this.getNode(nodeID);
      return node ? node.coordinates : [];
    },
    getBranchCoordinates(branch) {
      const fromNode = this.getNodeCoordinates(branch.node_from);
      const toNode = this.getNodeCoordinates(branch.node_to);
      const branchCoordinates = branch.coordinates ? branch.coordinates : [];
      return [fromNode, ...branchCoordinates, toNode];
    },
    circleIcon,
    latLngBounds,
    centerMap(ref, branch) {
      const bounds = latLngBounds([
        this.getNodeCoordinates(branch.node_from),
        this.getNodeCoordinates(branch.node_to),
      ]);
      const mapBounds = Object.keys(bounds).length ? bounds.pad(0.3) : bounds;
      this.$refs[ref].mapObject.fitBounds(mapBounds);
    },
    point(x, y) {
      return new Point(x, y);
    },
  },
};
</script>

<style></style>
